import { useEffect, useState } from "react";
import { useOutletContext } from "react-router-dom";
import api from "@/lib/api";
import { toast } from "sonner";
import LayeredStack from "@/components/LayeredStack";
import { Countdown } from "@/components/Kit";
import { Rocket, Coins, PackageOpen } from "lucide-react";

export default function Loops() {
  const { refreshUser } = useOutletContext();
  const [d, setD] = useState(null);

  const load = async () => { const { data } = await api.get("/loops"); setD(data); };
  useEffect(() => { load(); }, []);

  const dispatch = async (pet_id, hours) => {
    try { await api.post("/loops/dispatch", { pet_id, hours }); toast.success(`Dispatched on a ${hours}h salvage run.`); load(); }
    catch (e) { toast.error(e.response?.data?.detail || "Could not dispatch"); }
  };
  const collect = async (pet_id) => {
    try { const { data } = await api.post("/loops/collect", { pet_id }); toast.success(`+${data.bits} Bits${data.items.length ? ` & ${data.items.join(", ")}` : ""}`); refreshUser?.(); load(); }
    catch (e) { toast.error(e.response?.data?.detail || "Not ready"); }
  };

  if (!d) return null;
  return (
    <div data-testid="loops-page" className="max-w-3xl mx-auto">
      <span className="label-cap text-primary flex items-center gap-2"><Rocket className="w-4 h-4" /> Active Maintenance Loops</span>
      <h1 className="font-head font-black text-3xl sm:text-4xl mt-2 mb-1">Salvage Dispatch</h1>
      <p className="text-muted-foreground mb-5">Send idle robots on timed salvage routes. They return with Bits and random parts.</p>

      <div className="space-y-3">
        {d.robots.length === 0 && <p className="text-sm text-muted-foreground py-8 text-center">No robots yet — fabricate one first.</p>}
        {d.robots.map((r) => (
          <div key={r.pet_id} data-testid={`loop-robot-${r.pet_id}`} className="panel-glow p-4 flex items-center gap-4">
            <div className="rounded-lg overflow-hidden border border-border shrink-0"><LayeredStack layers={r.layers} core={r.core_color} rig={r.rig} cpu={r.cpu} size={56} state="idle" /></div>
            <div className="flex-1 min-w-0">
              <div className="font-head font-bold">{r.name}</div>
              {r.training ? <div className="label-cap text-accent">In training</div>
                : r.run ? (r.run.ready
                  ? <div className="label-cap text-emerald-400">Salvage complete!</div>
                  : <div className="label-cap">Returns in <Countdown seconds={r.run.remaining} onDone={load} className="text-secondary" /></div>)
                : <div className="label-cap text-muted-foreground">Idle — ready to dispatch</div>}
            </div>
            <div className="shrink-0">
              {r.run ? (
                <button data-testid={`loop-collect-${r.pet_id}`} onClick={() => collect(r.pet_id)} disabled={!r.run.ready}
                  className={r.run.ready ? "btn-primary" : "btn-ghost opacity-50"}><PackageOpen className="w-4 h-4" /> Collect</button>
              ) : r.training ? <span className="label-cap text-[9px]">busy</span> : (
                <div className="flex gap-1.5">
                  {d.options.map((o) => (
                    <button key={o.hours} data-testid={`loop-dispatch-${r.pet_id}-${o.hours}`} onClick={() => dispatch(r.pet_id, o.hours)}
                      className="btn-ghost text-xs py-1.5 px-2.5" title={`+${o.bits} Bits, ${o.items} item(s)`}>{o.hours}h</button>
                  ))}
                </div>
              )}
            </div>
          </div>
        ))}
      </div>
      <div className="panel p-3 mt-4 text-xs text-muted-foreground flex items-center gap-4">
        {d.options.map((o) => <span key={o.hours} className="flex items-center gap-1"><Coins className="w-3.5 h-3.5 text-secondary" /> {o.hours}h → {o.bits} Bits + {o.items} item(s)</span>)}
      </div>
    </div>
  );
}
